import React, { useEffect, useRef, useState } from "react";

// Text input + chevron + dropdown list. Replaces the native
// <input list={datalist}> pattern, which only shows a dropdown arrow on
// desktop browsers — iOS and most Android browsers don't render the
// datalist toggle, so mobile users had no visible affordance to surface
// the roster. This component renders an explicit chevron that works
// identically across platforms.
//
// Typing in the input still emits onChange (free text supported); when
// the dropdown is open, the visible list filters by the current value.

interface Option {
  id: string;
  name: string;
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  options: Option[];
  placeholder?: string;
  // Auto-focus the input on mount — used by the parent to put the
  // cursor in a freshly added blank row.
  autoFocus?: boolean;
  className?: string;
}

export default function RosterCombobox({
  value,
  onChange,
  options,
  placeholder,
  autoFocus,
  className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const trimmed = value.trim().toLowerCase();
  const visible = trimmed
    ? options.filter((o) => o.name.toLowerCase().includes(trimmed))
    : options;

  // Outside click / Escape → close. Skip when already closed so we
  // don't waste cycles registering listeners unnecessarily.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (wrapRef.current && wrapRef.current.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (name: string) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className={`cg-combo${className ? " " + className : ""}`}>
      <input
        type="text"
        className="cg-combo-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
      <button
        type="button"
        className="cg-combo-chevron"
        onClick={() => setOpen((o) => !o)}
        aria-label="Show roster"
        aria-expanded={open}
        tabIndex={-1}
      >
        ▾
      </button>
      {open && visible.length > 0 && (
        <ul className="cg-combo-list" role="listbox">
          {visible.map((o) => (
            <li
              key={o.id}
              className="cg-combo-item"
              role="option"
              aria-selected={o.name === value}
              onClick={() => pick(o.name)}
            >
              {o.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
